<template>
    <vx-page-layout layout="sidebar" size="2xl">
        <template v-slot:subtitle>
            <analytics-franchise-hub-page-header
                part="subtitle"
                :backButtonHref="`/analytics/franchise-hub/${props.category}/${props.metric}`"
                backButtonText="Back to Metric"
                :fetchParams="debouncedFetchParams"
                :category="props.category"
                :metric="props.metric"
                pageTitle="Groups"
            />
        </template>
        <template v-slot:title>
            <analytics-franchise-hub-page-header
                part="title"
                :backButtonHref="`/analytics/franchise-hub/${props.category}/${props.metric}`"
                backButtonText="Back to Metric"
                :fetchParams="debouncedFetchParams"
                :category="props.category"
                :metric="props.metric"
                pageTitle="Groups"
            />
        </template>
        <template v-slot:sidebar>
            <analytics-sidebar
                v-model="fetchParams"
                hideCategoryFilter
                hideResourceFilter
                hideLockedSnippetsFilter
                @reset="resetFilters()"
                :minAllowedDate="minAllowedDate"
            ></analytics-sidebar>
        </template>

        <div class="relative">
            <transition
                enterFromClass="opacity-0 -translate-y-4"
                enterToClass="opacity-100 translate-y-0"
                leaveFromClass="opacity-100 translate-y-0"
                leaveToClass="opacity-0 -translate-y-4"
            >
                <div v-if="loading" class="absolute w-full transition pt-20">
                    <loading-bars></loading-bars>
                </div>
            </transition>

            <div
                v-if="debouncedFetchParams"
                class="flex flex-col gap-6"
                :class="{
                    'opacity-0 pointer-events-none': loading,
                    'opacity-100 delay-200 transition duration-150': !loading,
                }"
            >
                <analytics-category-metric-groups-table
                    :fetchParams="debouncedFetchParams"
                    :metric="props.metric"
                    :category="props.category"
                    :filename="`voxie-${props.category}-groups`"
                    :groupIds="props.groupIds"
                    :target="props.target"
                    :aboveTarget="props.aboveTarget"
                />
            </div>
        </div>
    </vx-page-layout>
</template>

<script setup>
import { VxPageLayout } from '@voxie/frontend-components';
import AnalyticsSidebar from './AnalyticsSidebar.vue';
import { useDebouncedFetchParams } from '~/components/analytics/useDebouncedFetchParams';
import { getDateRange, getMinAllowedDate, resourceTypesOptions, metricsWithGoals } from '~/utils/analytics';
import { audienceTypes } from '~/constants/message';
import { SENDER_TYPES } from '~/constants/senderTypes';
import { analyticsCategoriesValues } from '~/constants/categories';
import LoadingBars from '~/components/general/LoadingBars.vue';
import AnalyticsCategoryMetricGroupsTable from './charts/time-series/AnalyticsCategoryMetricGroupsTable.vue';
import AnalyticsFranchiseHubPageHeader from '~/components/analytics/partial/AnalyticsFranchiseHubPageHeader.vue';

const props = defineProps({
    category: {
        type: String,
        required: true,
        validator: (category) => analyticsCategoriesValues.includes(category),
    },
    metric: {
        type: String,
        required: true,
        validator: (metric) => metricsWithGoals.includes(metric),
    },
    groupIds: {
        type: Array,
        required: false,
    },
    target: {
        type: Number,
        required: false,
    },
    aboveTarget: {
        type: Boolean,
        required: false,
    },
});

const defaultFilters = () => {
    const last30Days = getDateRange('last_30_days');

    return {
        range: [last30Days.start_date, last30Days.end_date],
        filters: {
            sender_types: SENDER_TYPES.map((senderType) => senderType.value),
            audience_types: audienceTypes().map((audienceType) => audienceType.name),
            resource_types: resourceTypesOptions.map((resourceType) => resourceType.value),
            resource_categories: undefined,
            resources: undefined,
        },
    };
};

const { fetchParams, loading, debouncedFetchParams, resetFilters } = useDebouncedFetchParams(defaultFilters());

const minAllowedDate = getMinAllowedDate(true);
</script>
